import React, { useEffect, useState, forwardRef } from "react";
import { Input, Select, Button, Form, Card } from "antd";
import FeatherIcon from "feather-icons-react";
import { X, PlusCircle } from "react-feather";
import {
  createSupplier,
  updateSupplier,
  getSupplier,
  getAllSupplierTypes,
  createSupplierType,
  fetchBrandsBySelectedClients,
} from "../../core/api/settings";
import AddCategory from "../../core/modals/inventory/addcategory";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
const { Option } = Select;

const SupplierDetailsView = forwardRef(
  ({ isVisible, onClose, supplierId, onResize, clientId }, ref) => {
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [form] = Form.useForm();
    const [supplierTypes, setSupplierTypes] = useState([]);
    const data = useSelector((state) => state.toggle_header);
    const [brands, setBrands] = useState([]);
    useEffect(() => {
      const sidebarWidth = isFullScreen ? window.innerWidth * 0.6 : 555;
      onResize(sidebarWidth);
    }, [isFullScreen, onResize]);

    useEffect(() => {
      const fetchSupplierTypes = async () => {
        try {
          const response = await getAllSupplierTypes();
          setSupplierTypes(response.data);
        } catch (err) {
          console.error("Failed to fetch supplier types", err);
        }
      };

      fetchSupplierTypes();

      if (supplierId) {
        const loadSupplierDetails = async () => {
          try {
            const response = await getSupplier(supplierId);
            form.setFieldsValue({
              ...response.data,
              type_id: response.data.type?.id,
            });
          } catch (err) {
            console.error("Failed to load supplier details", err);
          }
        };

        loadSupplierDetails();
      } else {
        form.resetFields();
      }
    }, [supplierId, form, isVisible]);

    const handleAddSupplierType = async (typeName) => {
      try {
        const response = await createSupplierType({ name: typeName });
        setSupplierTypes((prev) => [...prev, response.data]);
      } catch (err) {
        console.error("Failed to create new supplier type", err);
      }
    };

    const handleSubmit = async () => {
      try {
        const values = await form.validateFields();
        if (supplierId) {
          await updateSupplier(supplierId, values);
        } else {
          await createSupplier(values);
        }
        resetState();
        onClose(); // Close the sidebar after success
      } catch (err) {
        console.error("Failed to submit supplier", err);
      }
    };

    const resetState = () => {
      form.resetFields();
      setIsFullScreen(false);
    };
    const [modalProps, setModalProps] = useState({
      title: "",
      onSubmit: null,
    });

    useEffect(() => {
      const fetchBrands = async () => {
        try {
          if (clientId) {
            const response = await fetchBrandsBySelectedClients([clientId]);
            setBrands(response.data);
          }
        } catch (err) {
          console.error("Failed to fetch brands", err);
        }
      };

      fetchBrands();
    }, [clientId]);

    return (
      <div
        ref={ref}
        className={`right-sidebar ${isVisible ? "visible" : ""}`}
        style={{
          position: "fixed",
          top: data ? 0 : 66,
          right: 0,
          height: "100%",
          zIndex: 1050,
          width: isFullScreen ? "60%" : "555px",
          backgroundColor: "#fff",
          boxShadow: "0 0 3px rgba(0, 0, 0, 0.6)",
          overflowY: "auto",
          transition: "transform 0.3s ease-in-out",
          transform: isVisible ? "translateX(0)" : "translateX(100%)",
        }}
      >
        <div
          className="sidebar-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "15px",
            borderBottom: "1px solid #ddd",
          }}
        >
          <h5>{supplierId ? "Edit Supplier" : "Create New Supplier"}</h5>
          <div>
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="btn"
            >
              <FeatherIcon icon={isFullScreen ? "minimize" : "maximize"} />
            </button>

            <button
              className="btn"
              onClick={() => {
                resetState();
                onClose();
              }}
            >
              <X />
            </button>
          </div>
        </div>

        <div className="modal-body" style={{ padding: "15px" }}>
          <Form form={form} layout="vertical">
            <Card title="Supplier Information">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "15px",
                }}
              >
                <Form.Item name="brand_id" label="Brand">
                  <Select
                    placeholder="Select Brand"
                    value={form.getFieldValue("brand_id")}
                    onChange={(value) =>
                      form.setFieldsValue({ brand_id: value })
                    }
                    style={{ flex: 1 }}
                    rules={[
                      { required: true, message: "Please select a brand" },
                    ]}
                  >
                    {brands.map((brand) => (
                      <Option key={brand.id} value={brand.id}>
                        {brand.brand_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Supplier Name"
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the supplier name",
                    },
                  ]}
                >
                  <Input placeholder="Enter Supplier Name" />
                </Form.Item>
                <Form.Item label="Website" name="website">
                  <Input placeholder="Enter Website URL" />
                </Form.Item>
                <Form.Item
                  label="Type"
                  name="type_id"
                  rules={[{ required: true, message: "Please select a type" }]}
                >
                  {" "}
                  <Link
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#add-units-category"
                    onClick={() =>
                      setModalProps({
                        title: "Supplier Type",
                        onSubmit: async (typeName) => {
                          await handleAddSupplierType(typeName);
                        },
                      })
                    }
                  >
                    <PlusCircle className="plus-down-add" />
                    <span>Add New</span>
                  </Link>
                  <Select
                    placeholder="Select Type"
                    value={form.getFieldValue("type_id")}
                    onChange={(value) =>
                      form.setFieldsValue({ type_id: value })
                    }
                    style={{ flex: 1 }}
                  >
                    {supplierTypes.map((type) => (
                      <Option key={type.id} value={type.id}>
                        {type.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label="Address" name="address">
                  <Input.TextArea rows={4} placeholder="Enter address" />
                </Form.Item>
                <Form.Item label="Phone Number" name="phone_number">
                  <Input placeholder="Enter Phone Number" />
                </Form.Item>
                <Form.Item label="Email" name="email">
                  <Input placeholder="Enter Email Address" />
                </Form.Item>
                <Form.Item label="Contact Name" name="contact_name">
                  <Input placeholder="Enter Contact Name" />
                </Form.Item>
              </div>
            </Card>
            <div style={{ textAlign: "right", marginTop: "20px" }}>
              <Button
                type="primary"
                className="btn btn-primary"
                onClick={handleSubmit}
              >
                {supplierId ? "Update Supplier" : "Create Supplier"}
              </Button>
            </div>
          </Form>
        </div>
        <div style={{ height: 100 }}></div>
        <AddCategory {...modalProps}></AddCategory>
      </div>
    );
  }
);

SupplierDetailsView.displayName = "SupplierDetailsView";

export default SupplierDetailsView;
